import { Form } from "antd";
import dayjs from "dayjs";
import React, { forwardRef, useImperativeHandle } from "react";
import "./ChartEditForm.less";
import type { TemplateEditor } from "@/components/ui/RichTextEditor";
import {
  InvestigationsSection,
  PatientInfoSection,
  PreOpSection,
  VitalSignsSection,
} from "./index";
// Chart data interface
export interface ChartData {
  id: number;
  name: string;
  date: string;
  hrn: string;
  patientName: string;
  nric: string;
  drugAllergy: string;
  presentIllness: string;
  pastHistory: string;
  physicalExamination: string;
  temperature: string;
  systolic: string;
  diastolic: string;
  pulse: string;
  oximetry: string;
  weight: string;
  preOpOrders: {
    wholeBlood: boolean;
    redCells: boolean;
    platelets: boolean;
    others: boolean;
  };
  bloodCounts: {
    wholeBlood: string;
    redCells: string;
    platelets: string;
    others: string;
  };
  investigations: {
    preOp1: boolean;
    preOp2: boolean;
    coagulationProfile: boolean;
    ptptt: boolean;
    fbc: boolean;
    uecrs: boolean;
    ecg: boolean;
    cxr: boolean;
    others: boolean;
  };
  investigationsOthers: string;
  provisionalDiagnosis: string;
  examinedByDr: string;
}
interface ChartEditFormProps {
  initialData?: Partial<ChartData>;
  onSave: (data: ChartData) => void;
  onCancel: () => void;
}
// Define form values type
interface FormValues {
  date?: { format: (fmt: string) => string };
  hrn?: string;
  patientName?: string;
  nric?: string;
  drugAllergy?: string;
  presentIllness?: string;
  pastHistory?: string;
  temperature?: string;
  systolic?: string;
  diastolic?: string;
  pulse?: string;
  oximetry?: string;
  weight?: string;
  wholeBlood?: boolean;
  redCells?: boolean;
  platelets?: boolean;
  othersPreOp?: boolean;
  wholeBloodCount?: string;
  redCellsCount?: string;
  plateletsCount?: string;
  othersCount?: string;
  preOp1?: boolean;
  preOp2?: boolean;
  coagulationProfile?: boolean;
  ptptt?: boolean;
  fbc?: boolean;
  uecrs?: boolean;
  ecg?: boolean;
  cxr?: boolean;
  othersInvestigation?: boolean;
  investigationsOthers?: string;
  provisionalDiagnosis?: string;
  examinedByDr?: string;
}
const ChartEditForm = forwardRef<{ submit: () => void }, ChartEditFormProps>(
  ({ initialData, onSave }, ref) => {
    const [form] = Form.useForm();

    useImperativeHandle(ref, () => ({
      submit: () => {
        form.submit();
      },
    }));
    const [physicalExamEditor, setPhysicalExamEditor] = React.useState<TemplateEditor>({
      id: Date.now().toString(),
      content: initialData?.physicalExamination || "",
      name: "Physical Examination",
    });
    // Handle rich text editor content change
    const handleEditorContentChange = (_id: string | number, content: string) => {
      setPhysicalExamEditor({ ...physicalExamEditor, content });
    };
    // Form submission handler
    const handleSubmit = (values: FormValues) => {
      const formattedData: ChartData = {
        id: initialData?.id || Date.now(),
        name: initialData?.name || "ADMISSION_FORM",
        date: values.date ? values.date.format("DD MMM YYYY") : new Date().toLocaleDateString(),
        hrn: values.hrn || "",
        patientName: values.patientName || "",
        nric: values.nric || "",
        drugAllergy: values.drugAllergy || "",
        presentIllness: values.presentIllness || "",
        pastHistory: values.pastHistory || "",
        physicalExamination: physicalExamEditor.content,
        temperature: values.temperature || "",
        systolic: values.systolic || "",
        diastolic: values.diastolic || "",
        pulse: values.pulse || "",
        oximetry: values.oximetry || "",
        weight: values.weight || "",
        preOpOrders: {
          wholeBlood: values.wholeBlood || false,
          redCells: values.redCells || false,
          platelets: values.platelets || false,
          others: values.othersPreOp || false,
        },
        bloodCounts: {
          wholeBlood: values.wholeBloodCount || "",
          redCells: values.redCellsCount || "",
          platelets: values.plateletsCount || "",
          others: values.othersCount || "",
        },
        investigations: {
          preOp1: values.preOp1 || false,
          preOp2: values.preOp2 || false,
          coagulationProfile: values.coagulationProfile || false,
          ptptt: values.ptptt || false,
          fbc: values.fbc || false,
          uecrs: values.uecrs || false,
          ecg: values.ecg || false,
          cxr: values.cxr || false,
          others: values.othersInvestigation || false,
        },
        investigationsOthers: values.investigationsOthers || "",
        provisionalDiagnosis: values.provisionalDiagnosis || "",
        examinedByDr: values.examinedByDr || "",
      };
      onSave(formattedData);
    };
    return (
      <div className="chart-edit-form">
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            date: initialData?.date ? dayjs(initialData.date) : undefined,
            hrn: initialData?.hrn || "",
            patientName: initialData?.patientName || "",
            nric: initialData?.nric || "",
            drugAllergy: initialData?.drugAllergy || "",
            presentIllness: initialData?.presentIllness || "",
            pastHistory: initialData?.pastHistory || "",
            temperature: initialData?.temperature || "",
            systolic: initialData?.systolic || "",
            diastolic: initialData?.diastolic || "",
            pulse: initialData?.pulse || "",
            oximetry: initialData?.oximetry || "",
            weight: initialData?.weight || "",
            wholeBlood: initialData?.preOpOrders?.wholeBlood || false,
            redCells: initialData?.preOpOrders?.redCells || false,
            platelets: initialData?.preOpOrders?.platelets || false,
            othersPreOp: initialData?.preOpOrders?.others || false,
            wholeBloodCount: initialData?.bloodCounts?.wholeBlood || "",
            redCellsCount: initialData?.bloodCounts?.redCells || "",
            plateletsCount: initialData?.bloodCounts?.platelets || "",
            othersCount: initialData?.bloodCounts?.others || "",
            preOp1: initialData?.investigations?.preOp1 || false,
            preOp2: initialData?.investigations?.preOp2 || false,
            coagulationProfile: initialData?.investigations?.coagulationProfile || false,
            ptptt: initialData?.investigations?.ptptt || false,
            fbc: initialData?.investigations?.fbc || false,
            uecrs: initialData?.investigations?.uecrs || false,
            ecg: initialData?.investigations?.ecg || false,
            cxr: initialData?.investigations?.cxr || false,
            othersInvestigation: initialData?.investigations?.others || false,
            investigationsOthers: initialData?.investigationsOthers || "",
            provisionalDiagnosis: initialData?.provisionalDiagnosis || "",
            examinedByDr: initialData?.examinedByDr || "",
          }}
          onFinish={handleSubmit}
          className="admission-form"
        >
          <div className="form-header"></div>

          <PatientInfoSection
            physicalExamEditor={physicalExamEditor}
            onEditorContentChange={handleEditorContentChange}
          />

          <VitalSignsSection />

          <PreOpSection />

          <InvestigationsSection />
        </Form>
      </div>
    );
  },
);

ChartEditForm.displayName = "ChartEditForm";
export default ChartEditForm;
